'use strict';

import MacrosPieChartV2 from '@components/Widgets/MacrosPieChartV2.react';
import { getPrescriptionsFromProfile } from '@pro/utils/Patients';
import AuthStore from '@stores/AuthStore';
import NutritionPatternStore from '@stores/NutritionPatternStore';
import allNutrients from '@tables/nutrients';
import { getConfig } from '@utils/Env';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import ReactMarkdown from 'react-markdown';
import Modal from 'react-modal';
import '../../FeedModals.scss';
import './Coachmark.scss';

export default class Coachmark extends Component {
    static propTypes = {};

    static contextTypes = {
        isMobile: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        let pattern = {};

        const {
            conditions,
            hide_nutrition,
            hide_ewg,
            inhibit_swap,
            inhibit_change_avoidances,
            inhibit_autopopulate,
            inhibit_change_nutrition_profile,
        } = props.user;

        const isSafeMode =
            hide_nutrition ||
            hide_ewg ||
            inhibit_swap ||
            inhibit_change_avoidances ||
            inhibit_autopopulate ||
            inhibit_change_nutrition_profile;
        const embedOptions = {
            videoFoam: true,
            settingsControl: true,
            fullscreenButton: true,
            playbar: true,
            volumeControl: true,
        };
        const uriEncodedOptions = new URLSearchParams(embedOptions).toString();
        const videoUrl = isSafeMode
            ? `https://fast.wistia.net/embed/iframe/n0dzr2wjb5?${uriEncodedOptions}`
            : `https://fast.wistia.net/embed/iframe/b64ej4t1s6?${uriEncodedOptions}`;

        const nutritionPatterns = NutritionPatternStore.getNutritionPatterns();

        if (!isEmpty(conditions) && !isEmpty(nutritionPatterns)) {
            pattern = nutritionPatterns.find((pattern) => pattern.uuid === conditions[0].uuid);
        }

        this.state = {
            drawer: false,
            quickTour: false,
            loading: false,
            pattern,
            videoUrl,
            closing: false,
        };
    }

    componentDidMount = () => {
        this.setState({ drawer: true });
        this.loadPattern();
    };

    loadPattern = async () => {
        const { pattern } = this.state;
        const {
            user: { conditions },
        } = this.props;
        let nutritionPattern = {};

        if (isEmpty(pattern) && !isEmpty(conditions)) {
            this.setState({ loading: true });

            nutritionPattern = await AuthStore.fetch({
                url: getConfig('users_api') + '/nutrition-patterns' + `/${conditions[0].uuid}`,
            });

            this.setState({ pattern: nutritionPattern, loading: false });
        }
    };

    closeModal = () => {
        // Close the coachmark modal, then notify parent (Dashboard splash handles the transition)
        this.setState({ drawer: false, quickTour: false, closing: true }, () => {
            setTimeout(this.props.closeModal, 333);
        });
    };

    openQuickTour = () => {
        this.setState({ quickTour: true });
    };

    closeQuickTour = () => {
        this.setState({ quickTour: false });
    };

    renderContent = () => {
        const { pattern } = this.state;
        const { user } = this.props;
        const { isMobile } = this.context;
        const { conditions, hide_nutrition } = user;
        const { macros } = getPrescriptionsFromProfile(user);

        // Get focused nutrients from daily_totals envelope
        const focusedNutrientIds = [];
        const dailyTotals = user.preferences?.daily_totals || {};

        // Always include calories (208) first if it exists
        if (dailyTotals['208']) {
            focusedNutrientIds.push('208');
        }


        // Add other focused nutrients
        Object.keys(dailyTotals).forEach((nutrNo) => {
            if (dailyTotals[nutrNo].focused && nutrNo !== '208') {
                focusedNutrientIds.push(nutrNo);
            }
        });

        const focusedNutrients = focusedNutrientIds.map((nutrNo) => {
            const name = allNutrients[nutrNo].NutrDesc;
            const unit = allNutrients[nutrNo].Units;

            return {
                nutrNo,
                name,
                unit,
                ...dailyTotals[nutrNo]
            };
        });

        return (
            <>
                <div className="separator">
                    <div className="your-prescription">YOUR PRESCRIPTION</div>
                </div>

                <div className="description">
                    <div>
                        <h3>{conditions[0].name}</h3>
                        {pattern && pattern.description ? <p>{pattern.description}</p> : null}
                    </div>
                    {!isMobile ? (
                        <button className="el-btn el-medium-btn el-orange-btn el-wide-btn" onClick={this.closeModal}>
                            Start Your Plan
                        </button>
                    ) : null}
                </div>

                <div className="separator"></div>

                {/* Show nutrition section only if not hidden and either has macros (when not inhibited) or has focused nutrients */}
                {!hide_nutrition && (!(isEmpty(macros) && pattern?.inhibit_focused_nutrients) || focusedNutrients.length > 0) ? (
                    <>
                        <h5>Your daily focus</h5>
                        <div className="nutrients">
                            <MacrosPieChartV2
                                nutrients={macros}
                                focusedNutrients={focusedNutrients}
                                hideMacros={pattern?.inhibit_focused_nutrients}
                            />
                        </div>
                        <div className="separator"></div>
                    </>
                ) : null}

                {pattern && pattern.guidelines ? (
                    <div className="what-to-know">
                        <h5>WHAT TO KNOW</h5>
                        <ReactMarkdown source={pattern.guidelines} />
                    </div>
                ) : null}
            </>
        );
    };

    renderWelcomeScreen = () => {
        const { drawer, pattern, quickTour, videoUrl, closing } = this.state;
        const { user } = this.props;
        const { isMobile } = this.context;
        const { first_name } = user;

        return (
            <Modal
                isOpen={!quickTour && !closing}
                closeModal={() => false}
                onRequestClose={() => false}
                className={drawer ? 'feed-coachmark-modal open' : 'feed-coachmark-modal'}
                overlayClassName="feed-modal-overlay"
                contentLabel="EatLove coachmark"
                closeTimeoutMS={250}
            >
                <div className="feed-coachmark-modal-container">
                    <h1>
                        <em>{first_name}</em>, welcome to{' '}
                        <img src="https://static.chewba.info/images/header-logo-white-2017-07-03.png" />
                    </h1>
                    <div className="feed-coachmark feed-coachmark-styles" onClick={(ev) => ev.stopPropagation()}>
                        {isEmpty(pattern) ? (
                            <span className="coachmark-loader">
                                <i className="icon-spinner2" />
                            </span>
                        ) : (
                            this.renderContent()
                        )}
                    </div>
                    {!isEmpty(pattern) ? (
                        <div className="iframe-container" onClick={this.openQuickTour}>
                            <div className="title">Take a quick tour</div>
                            <div className="iframe-overlay"></div>
                            <iframe
                                src={videoUrl}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    ) : null}
                    {isMobile && !isEmpty(pattern) ? (
                        <footer>
                            <button
                                className="el-btn el-large-btn el-raspberry-btn el-btn-icon-left"
                                onClick={this.openQuickTour}
                            >
                                <i className="icon-right-arrow" />
                                TOUR
                            </button>
                            <button className="el-btn el-large-btn el-orange-btn el-wide-btn" onClick={this.closeModal}>
                                Start Your Plan
                            </button>
                        </footer>
                    ) : null}
                </div>
            </Modal>
        );
    };

    renderQuickTour = () => {
        const { quickTour, videoUrl } = this.state;

        if (!quickTour) {
            return;
        }

        return (
            <Modal
                isOpen={true}
                closeModal={this.closeQuickTour}
                onRequestClose={this.closeQuickTour}
                className={'feed-coachmark-modal'}
                overlayClassName="feed-modal-overlay"
                contentLabel="EatLove coachmark"
                closeTimeoutMS={250}
            >
                <div className="feed-coachmark-modal-container quick-tour">
                    <header>
                        <h1>Take a Quick Tour</h1>
                        <button className="close-btn" onClick={this.closeQuickTour}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="video-container">
                        <iframe
                            src={videoUrl}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>

                    <footer>
                        <button className="el-btn el-large-btn el-orange-btn el-wide-btn" onClick={this.closeModal}>
                            Start Your Plan
                        </button>
                    </footer>
                </div>
            </Modal>
        );
    };

    render = () => {
        return (
            <>
                {this.renderWelcomeScreen()}
                {this.renderQuickTour()}
            </>
        );
    };
}
